import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import clsx from 'clsx';
import { Camera, Check, ClipboardList, CloudOff, Image, ImageOff, Maximize2, Package, Plus, RefreshCw, Trash } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, PagedResponse, StockOpname, StockOpnameStatus, StockRequestProduct } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants';
import { formatFileSize } from '@/utils/formatters';
import { shrinkImage } from '@/utils/stockRequest';
import { classifyStockOpnameError, productIdFromMessage, varianceOf } from '@/utils/stockOpname';
import { Alert, Badge, Button, Chip, IconButton, Input, LoadingSkeleton, Overline, SearchPill, Sheet } from '@/components/ui';
import { PageShell } from '@/components/layout/PageShell';
import { useIsLandscape } from '@/hooks/useMediaQuery';

const MAX_IMAGE_BYTES = 1 * 1024 * 1024;
const PAGE_SIZE = 20;
type Filter = 'ALL' | StockOpnameStatus;
const FILTERS: Filter[] = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'];
const STATUS_TONE: Record<StockOpnameStatus, 'warning' | 'success' | 'danger'> = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger' };
const draftKey = (shopId: number) => `stockOpname:draft:${shopId}`;

/** Stock-opname routes return meta.errors as a plain string on failure. */
const apiError = (err: unknown) => {
    if (axios.isAxiosError(err)) {
        const errors = err.response?.data?.meta?.errors;
        if (typeof errors === 'string' && errors) return errors;
    }
    return getErrorMessage(err);
};

/** createdAt is a naive server-local string; formatters' toLocalDate would shift it by +7h. */
const whenOf = (iso: string) => new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const openProof = (url: string) => window.open(url, '_blank', 'noopener');
const fmtQty = (n: number) => n.toLocaleString('id-ID', { maximumFractionDigits: 2 });
const fmtVariance = (v: number) => (v > 0 ? `+${fmtQty(v)}` : fmtQty(v));
const varianceTone = (v: number | null) => (v == null || v === 0 ? 'text-ink-muted' : v < 0 ? 'text-status-danger' : 'text-success-700');
const countOf = (s: string | undefined) => (s == null || s.trim() === '' || !Number.isFinite(Number(s)) ? null : Number(s));

const readDraft = (shopId: number): Record<number, string> => {
    try { return JSON.parse(localStorage.getItem(draftKey(shopId)) || '{}'); } catch { return {}; }
};

/**
 * Stock opname (physical count) — list with a detail pane (lg) / sheet (phone), and a count sheet
 * (search · rows with system/counted/variance · optional note/photo) as a second view. Mirrors StockRequestPage.
 */
const StockOpnamePage: React.FC = () => {
    const { t } = useTranslation();
    const { selectedShop } = useAppSelector((s) => s.auth);
    const isLandscape = useIsLandscape();
    const shopId = selectedShop?.shopId;
    const [products, setProducts] = useState<StockRequestProduct[]>([]);
    const [entries, setEntries] = useState<StockOpname[]>([]);
    const [page, setPage] = useState(0);
    const [last, setLast] = useState(true);
    const [filter, setFilter] = useState<Filter>('ALL');
    const [loading, setLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [view, setView] = useState<'list' | 'submit'>('list');
    // count sheet
    const [counts, setCounts] = useState<Record<number, string>>(() => (shopId ? readDraft(shopId) : {}));
    const [search, setSearch] = useState('');
    const [note, setNote] = useState('');
    const [photo, setPhoto] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [photoError, setPhotoError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [uploadFailed, setUploadFailed] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    const loadProducts = useCallback(async () => {
        if (!shopId) return;
        try {
            const res = await api.get<ApiResponse<StockRequestProduct[]>>(API_ENDPOINTS.STOCK_REQUEST_PRODUCTS(shopId));
            setProducts(res.data.data || []);
        } catch (e) {
            setError(apiError(e));
        }
    }, [shopId]);

    const load = useCallback(async (p: number, replace: boolean) => {
        if (!shopId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await api.get<ApiResponse<PagedResponse<StockOpname>>>(API_ENDPOINTS.STOCK_OPNAME(shopId), {
                params: { status: filter === 'ALL' ? undefined : filter, page: p, size: PAGE_SIZE },
            });
            const content = res.data.data.content || [];
            setEntries((prev) => (replace ? content : [...prev, ...content]));
            setLast(res.data.data.page?.last ?? content.length < PAGE_SIZE);
            setPage(p);
        } catch (e) {
            setError(apiError(e));
        } finally {
            setLoading(false);
        }
    }, [shopId, filter]);

    const refresh = useCallback(() => { loadProducts(); load(0, true); }, [loadProducts, load]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        if (!shopId) return;
        try {
            if (Object.keys(counts).length) localStorage.setItem(draftKey(shopId), JSON.stringify(counts));
            else localStorage.removeItem(draftKey(shopId));
        } catch { /* storage unavailable: draft is a convenience only */ }
    }, [counts, shopId]);
    useEffect(() => {
        if (!photo) { setPreview(null); return; }
        const url = URL.createObjectURL(photo);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [photo]);

    const selected = entries.find((e) => e.id === selectedId) ?? (isLandscape ? entries[0] : undefined) ?? null;
    const selectedLines = selected?.lines;
    // Header rows arrive with lines: null; the detail route fills them in.
    useEffect(() => {
        if (!shopId || !selected || selectedLines) return;
        const id = selected.id;
        let stale = false;
        setDetailLoading(true);
        api.get<ApiResponse<StockOpname>>(API_ENDPOINTS.STOCK_OPNAME_DETAIL(shopId, id))
            .then((res) => { if (!stale) setEntries((prev) => prev.map((e) => (e.id === id ? res.data.data : e))); })
            .catch((e) => { if (!stale) toast.error(apiError(e)); })
            .finally(() => { if (!stale) setDetailLoading(false); });
        return () => { stale = true; };
    }, [shopId, selected?.id, selectedLines]);

    if (!selectedShop || !shopId) return null;

    const photoTooLarge = !!photo && photo.size > MAX_IMAGE_BYTES;
    const noProducts = products.length === 0;
    const filled = products.filter((p) => countOf(counts[p.productPointId]) != null);
    const negative = filled.some((p) => countOf(counts[p.productPointId])! < 0);
    const varianceCount = filled.filter((p) => varianceOf(countOf(counts[p.productPointId])!, p.stock) !== 0).length;
    const canSubmit = filled.length > 0 && !negative && !photoTooLarge && !noProducts;
    const q = search.trim().toLowerCase();
    const visible = q ? products.filter((p) => p.productName.toLowerCase().includes(q)) : products;

    const pickPhoto = async (f: File | null) => {
        const file = f ? await shrinkImage(f, MAX_IMAGE_BYTES) : null;
        setPhoto(file);
        setPhotoError(file && file.size > MAX_IMAGE_BYTES ? t('stockOpname.photoTooLarge') : null);
        setUploadFailed(false);
    };
    const setCount = (id: number, v: string) => { setCounts((c) => ({ ...c, [id]: v })); setFormError(null); };
    const resetForm = () => { setCounts({}); setNote(''); setSearch(''); setPhoto(null); setPhotoError(null); setFormError(null); setUploadFailed(false); };
    const statusLabel = (s: StockOpnameStatus) => t(`stockOpname.status.${s}` as 'stockOpname.status.PENDING');

    const submit = async () => {
        setConfirmOpen(false);
        setFormError(null);
        if (filled.length === 0) { setFormError(t('stockOpname.linesRequired')); return; }
        if (negative) { setFormError(t('stockOpname.countedInvalid')); return; }
        if (photoTooLarge) { setPhotoError(t('stockOpname.photoTooLarge')); return; }
        setSubmitting(true);
        try {
            const form = new FormData();
            // Plain string: the BFF binds `lines` as a request param, a JSON Blob would become a file part.
            form.append('lines', JSON.stringify(filled.map((p) => ({ productPointId: p.productPointId, countedStock: countOf(counts[p.productPointId]) }))));
            if (note.trim()) form.append('note', note.trim());
            if (photo) form.append('proof', photo, 'proof.jpg');
            await api.post<ApiResponse<StockOpname>>(API_ENDPOINTS.STOCK_OPNAME(shopId), form, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(t('stockOpname.submitted'));
            resetForm();
            setView('list');
            if (filter === 'ALL') refresh(); else setFilter('ALL'); // filter change refetches via the effect
        } catch (e) {
            const msg = apiError(e);
            switch (classifyStockOpnameError(msg)) {
                case 'lines': setFormError(t('stockOpname.linesRequired')); break;
                case 'countedStock': setFormError(t('stockOpname.countedInvalid')); break;
                case 'product': {
                    const id = productIdFromMessage(msg);
                    if (id != null) setCounts(({ [id]: _dropped, ...rest }) => rest);
                    setFormError(t('stockOpname.productNotInShop'));
                    loadProducts();
                    break;
                }
                case 'photoTooLarge': setPhotoError(t('stockOpname.photoTooLarge')); setPhoto(null); break;
                case 'uploadFailed': setUploadFailed(true); break;
                case 'photo': setPhotoError(msg); setPhoto(null); break;
                case 'inactive': setFormError(t('stockOpname.inactiveUser')); break;
                default: setFormError(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const detail = (r: StockOpname) => {
        const facts: [string, string][] = [
            [t('stockOpname.recordedBy'), r.requesterName ?? r.requesterClientId ?? '—'],
            [t('stockOpname.time'), whenOf(r.createdAt)],
            [t('stockOpname.reviewedAt'), r.reviewedAt ? whenOf(r.reviewedAt) : '—'],
        ];
        return (
            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2.5">
                    <Overline className="flex-1">#{r.id}</Overline>
                    <Badge tone={STATUS_TONE[r.status]} dot>{statusLabel(r.status)}</Badge>
                </div>
                <div className="flex flex-col gap-1">
                    <span className="text-[38px] font-black tnum leading-[1.1] tracking-[-0.02em] text-ink-brand">{t('stockOpname.lineCount', { n: r.lineCount })}</span>
                    <span className={clsx('text-[19px] font-black', r.varianceCount > 0 ? 'text-status-danger' : 'text-ink-muted')}>{t('stockOpname.varianceCount', { n: r.varianceCount })}</span>
                    {r.note && <span className="text-sm leading-relaxed text-ink-secondary">{r.note}</span>}
                </div>
                {r.status === 'REJECTED' && r.reviewerNote && <Alert variant="error" title={t('stockOpname.rejectedNote')} message={r.reviewerNote} />}
                {r.status === 'PENDING' && <Alert variant="warning" message={t('stockOpname.pendingHint')} />}
                {r.proofImageUrl && (
                    <button type="button" onClick={() => openProof(r.proofImageUrl!)} className="relative h-[168px] shrink-0 overflow-hidden rounded-lg border border-line-subtle bg-surface-sunken">
                        <img src={r.proofImageUrl} alt="" className="h-full w-full object-cover" />
                        <span className="absolute bottom-2.5 right-2.5 flex h-[30px] items-center gap-1.5 rounded-pill bg-[#2d1227]/70 px-3 text-xs font-extrabold text-white backdrop-blur"><Maximize2 className="h-[13px] w-[13px]" />{t('stockOpname.viewProof')}</span>
                    </button>
                )}
                <div className="flex flex-col border-t border-line-subtle">
                    {facts.map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-3 border-b border-line-subtle py-2.5 text-sm"><span className="text-ink-muted">{k}</span><span className="text-right font-extrabold">{v}</span></div>
                    ))}
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_64px_64px_64px] gap-2 text-[11px] font-extrabold uppercase tracking-wide text-ink-muted">
                    <span /><span className="text-right">{t('stockOpname.system')}</span><span className="text-right">{t('stockOpname.counted')}</span><span className="text-right">{t('stockOpname.variance')}</span>
                </div>
                {!r.lines && detailLoading && [1, 2, 3].map((i) => <LoadingSkeleton key={i} height={36} />)}
                {r.lines?.map((l) => (
                    <div key={l.productPointId} className="grid grid-cols-[minmax(0,1fr)_64px_64px_64px] items-center gap-2 border-b border-line-subtle py-2 text-sm">
                        <span className="truncate font-bold">{l.productName}</span>
                        <span className="text-right tnum">{fmtQty(l.systemStock)}</span>
                        <span className="text-right font-extrabold tnum">{fmtQty(l.countedStock)}</span>
                        <span className={clsx('text-right font-extrabold tnum', varianceTone(l.variance))}>{fmtVariance(l.variance)}</span>
                    </div>
                ))}
            </div>
        );
    };

    if (view === 'submit') {
        const photoSlot = photoTooLarge ? (
            <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 rounded-xl border-2 border-status-danger bg-status-danger-soft p-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-card text-status-danger"><ImageOff className="h-7 w-7" /></span>
                <span className="text-[17px] font-black text-danger-700">{t('stockOpname.photoTooLarge')} · {formatFileSize(photo!.size)}</span>
                <span className="text-[13px] leading-relaxed">{t('stockOpname.photoTooLargeHint')}</span>
            </div>
        ) : preview ? (
            <div className="relative min-h-[180px] overflow-hidden rounded-xl border border-line-subtle bg-surface-sunken">
                <img src={preview} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <span className="absolute left-3 top-3 flex h-7 items-center gap-1.5 rounded-pill bg-surface-card px-2.5 text-xs font-extrabold text-success-700 shadow-xs"><Check className="h-[13px] w-[13px]" />{formatFileSize(photo!.size)}</span>
            </div>
        ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className={clsx('flex min-h-[180px] flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed bg-surface-card p-6 text-center hover:border-accent-500 hover:bg-accentsoft', photoError ? 'border-status-danger' : 'border-line')}>
                <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-accent-500 text-ink-onAccent shadow-accent"><Camera className="h-[30px] w-[30px]" /></span>
                <span className="text-[17px] font-black">{t('stockOpname.takePhoto')}</span>
                <span className="text-[13px] leading-relaxed text-ink-muted">{t('stockOpname.photoHint')}</span>
            </button>
        );
        const cta = (
            <div className="flex flex-col gap-2">
                {formError && <p className="text-xs font-semibold text-status-danger">{formError}</p>}
                <Button variant="primary" size="lg" fullWidth disabled={!canSubmit} loading={submitting} onClick={() => setConfirmOpen(true)}>{uploadFailed ? t('stockOpname.resend') : t('stockOpname.send')}</Button>
            </div>
        );
        return (
            <PageShell
                close
                title={t('stockOpname.submit')}
                subtitle={selectedShop.shopName}
                onBack={() => setView('list')}
                footer={<div className="lg:hidden">{cta}</div>}
            >
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { pickPhoto(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                <div className="flex flex-col gap-4 pb-5 lg:grid lg:h-full lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-5 lg:pb-6">
                    <div className="flex flex-col gap-3 lg:min-h-0 lg:overflow-y-auto">
                        {noProducts && <Alert variant="warning" message={t('stockOpname.noProducts')} />}
                        <SearchPill value={search} onChange={setSearch} onClear={() => setSearch('')} placeholder={t('stockOpname.search')} />
                        <div className="grid grid-cols-[minmax(0,1fr)_72px_96px_64px] gap-2 px-1 text-[11px] font-extrabold uppercase tracking-wide text-ink-muted">
                            <span /><span className="text-right">{t('stockOpname.system')}</span><span className="text-center">{t('stockOpname.counted')}</span><span className="text-right">{t('stockOpname.variance')}</span>
                        </div>
                        {visible.map((p) => {
                            const counted = countOf(counts[p.productPointId]);
                            const variance = counted == null ? null : varianceOf(counted, p.stock);
                            const bad = counted != null && counted < 0;
                            return (
                                <div key={p.productPointId} className={clsx('grid grid-cols-[minmax(0,1fr)_72px_96px_64px] items-center gap-2 rounded-lg border bg-surface-card px-2 py-2', bad ? 'border-status-danger' : 'border-line-subtle')}>
                                    <span className="flex min-w-0 items-center gap-2.5">
                                        {p.image ? <img src={p.image} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover" /> : <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-surface-sunken text-ink-muted"><Package className="h-5 w-5" /></span>}
                                        <span className="flex min-w-0 flex-col">
                                            <span className="truncate text-sm font-extrabold">{p.productName}</span>
                                            {p.usageUnit && <span className="truncate text-xs text-ink-muted">{t('stockOpname.unitHint', { n: fmtQty(p.stock * p.stockUsage), unit: p.usageUnit })}</span>}
                                        </span>
                                    </span>
                                    <span className="text-right text-sm font-bold tnum">{fmtQty(p.stock)}</span>
                                    <input
                                        type="number" step="any" min="0" inputMode="decimal" placeholder="—"
                                        value={counts[p.productPointId] ?? ''}
                                        onChange={(e) => setCount(p.productPointId, e.target.value)}
                                        aria-label={`${t('stockOpname.counted')} ${p.productName}`}
                                        className={clsx('h-11 w-full rounded-md border bg-surface-sunken px-2 text-center text-base font-extrabold tnum outline-none focus:border-accent-500 focus:shadow-focus', bad ? 'border-status-danger' : 'border-line')}
                                    />
                                    <span className={clsx('text-right text-sm font-extrabold tnum', varianceTone(variance))}>{variance == null ? '—' : fmtVariance(variance)}</span>
                                </div>
                            );
                        })}
                        {negative && <p className="text-xs font-semibold text-status-danger">{t('stockOpname.countedInvalid')}</p>}
                    </div>
                    <div className="flex flex-col gap-4 lg:min-w-0">
                        {uploadFailed && <Alert variant="error" title={t('stockOpname.uploadFailed')} message={<span className="flex items-center gap-2"><CloudOff className="h-4 w-4 shrink-0" />{t('stockOpname.uploadFailedHint')}</span>} />}
                        <div className="flex flex-col gap-2.5">
                            <span className="text-[13px] font-extrabold text-ink-secondary">{t('stockOpname.photo')}</span>
                            {photoSlot}
                            {photoError && !photoTooLarge && <p className="text-xs font-semibold text-status-danger">{photoError}</p>}
                            {photo && (
                                <div className="flex gap-2.5">
                                    <Button variant={photoTooLarge ? 'primary' : 'secondary'} fullWidth icon={<Camera />} onClick={() => fileRef.current?.click()}>{t('stockOpname.retake')}</Button>
                                    {!photoTooLarge && <Button variant="ghost" icon={<Trash />} onClick={() => pickPhoto(null)}>{t('stockOpname.remove')}</Button>}
                                </div>
                            )}
                        </div>
                        <Input label={t('stockOpname.note')} multiline rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('stockOpname.notePlaceholder')} />
                        <div className="hidden flex-1 lg:block" />
                        <Alert variant="brand" message={t('stockOpname.submitHint')} />
                        <div className="hidden lg:block">{cta}</div>
                    </div>
                </div>
                <Sheet
                    isOpen={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    title={t('stockOpname.confirmTitle')}
                    size="sm"
                    footer={<div className="flex gap-2.5"><Button variant="secondary" fullWidth onClick={() => setConfirmOpen(false)}>{t('stockOpname.cancel')}</Button><Button variant="primary" fullWidth onClick={submit}>{t('stockOpname.send')}</Button></div>}
                >
                    <p className="text-sm leading-relaxed">{t('stockOpname.confirmBody', { n: filled.length, v: varianceCount })}</p>
                </Sheet>
            </PageShell>
        );
    }

    const submitButton = (props: { size?: 'md' | 'lg'; fullWidth?: boolean; className?: string }) => (
        <Button variant="primary" icon={<Plus />} onClick={() => setView('submit')} disabled={noProducts} {...props}>{t('stockOpname.submit')}</Button>
    );

    return (
        <div className="flex h-full min-h-0 flex-1">
            <PageShell
                title={t('stockOpname.title')}
                subtitle={selectedShop.shopName}
                backTo="/settings"
                className="lg:border-r lg:border-line-subtle"
                actions={<>
                    <IconButton variant="card" label={t('common.refresh')} onClick={refresh} disabled={loading}><RefreshCw className={clsx(loading && 'animate-spin')} /></IconButton>
                    {submitButton({ className: 'hidden lg:inline-flex' })}
                </>}
                footer={<div className="lg:hidden">{submitButton({ size: 'lg', fullWidth: true })}</div>}
            >
                <div className="flex flex-col gap-3 pb-5">
                    {error && <Alert variant="error" message={error} />}
                    {!error && noProducts && <Alert variant="warning" message={t('stockOpname.noProducts')} />}
                    <div className="flex gap-2 overflow-x-auto">
                        {FILTERS.map((f) => (
                            <Chip key={f} selected={filter === f} onClick={() => setFilter(f)}>{f === 'ALL' ? t('stockOpname.filterAll') : statusLabel(f)}</Chip>
                        ))}
                    </div>
                    {loading && entries.length === 0 && [1, 2, 3].map((i) => <LoadingSkeleton key={i} height={72} />)}
                    {!loading && entries.length === 0 && (
                        <div className="flex flex-col items-center gap-3 py-12 text-center">
                            <span className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-brandsoft text-ink-brand"><ClipboardList className="h-[38px] w-[38px]" /></span>
                            <span className="text-[22px] font-black">{t('stockOpname.empty')}</span>
                            <span className="max-w-[380px] text-[15px] leading-relaxed text-ink-secondary">{t('stockOpname.emptyHint')}</span>
                        </div>
                    )}
                    {entries.map((r) => {
                        const active = isLandscape && selected?.id === r.id;
                        return (
                            <button
                                key={r.id}
                                type="button"
                                onClick={() => { setSelectedId(r.id); if (!isLandscape) setDetailOpen(true); }}
                                className={clsx('flex items-center gap-3.5 rounded-lg px-4 py-3 text-left', active ? 'border-2 border-accent-500 bg-accentsoft' : 'border border-line-subtle bg-surface-card')}
                            >
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-ink-brand"><ClipboardList className="h-5 w-5" /></span>
                                <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                                    <span className="truncate text-base font-extrabold">{whenOf(r.createdAt)}</span>
                                    <span className="flex items-center gap-1.5 text-[13px] text-ink-muted">{r.proofImageUrl && <Image className="h-3.5 w-3.5" />}{r.requesterName ?? r.requesterClientId ?? '—'}</span>
                                    {r.status === 'REJECTED' && r.reviewerNote && <span className="truncate text-xs text-ink-muted">{r.reviewerNote}</span>}
                                </span>
                                <span className="flex flex-col items-end gap-1.5">
                                    <span className="flex items-center gap-1.5 text-sm font-black tnum"><span>{t('stockOpname.lineCount', { n: r.lineCount })}</span><span className={r.varianceCount > 0 ? 'text-status-danger' : 'text-ink-muted'}>· {t('stockOpname.varianceCount', { n: r.varianceCount })}</span></span>
                                    <Badge tone={STATUS_TONE[r.status]} dot>{statusLabel(r.status)}</Badge>
                                </span>
                            </button>
                        );
                    })}
                    {!last && <Button variant="outline" onClick={() => load(page + 1, false)} loading={loading} className="mt-2 self-center">{t('common.loadMore')}</Button>}
                </div>
            </PageShell>

            <aside className="hidden w-[420px] shrink-0 overflow-y-auto bg-surface-card p-6 lg:block">
                {selected ? detail(selected) : (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-ink-muted">
                        <ClipboardList className="h-9 w-9 text-primary-200" />
                        <p className="text-sm font-bold">{t('stockOpname.selectHint')}</p>
                    </div>
                )}
            </aside>

            <Sheet isOpen={detailOpen && !isLandscape && !!selected} onClose={() => setDetailOpen(false)} showCloseButton={false}>
                {selected && detail(selected)}
            </Sheet>
        </div>
    );
};

export default StockOpnamePage;
